// eslint-disable-next-line jira/filename-case
import { useCallback } from 'react';
import { buildNonContextualNote } from '@atlassian/cross-join-nudges/services/non-contextual-notes';
import { useAtlassianAccount } from '@jira/platform__controllers-atlassian-account/src/controllers/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { isFetchError } from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { isClientFetchError } from '@atlassian/jira-fetch/src/utils/is-error.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useUserFullname } from '@atlassian/jira-tenant-context-controller/src/components/user-fullname/index.tsx';
import { useCurrentProduct } from '@jira/growth__invite-prompt-on-link-insert/src/utils/index.tsx';
import { requestAccess, useRequestAccessService } from '../../services/access-request/index.tsx';
import type { AccessRequestType } from '../../common/types.tsx';
import { fireErrorAnalytics } from '../../services/analytics/index.tsx';
import { useSnooze } from '../../services/caching/index.tsx';
import { useFilteredUsersFromCollaborationGraph } from '../../services/get-collaborators/index.tsx';
import { useNudgeStateJiraToLoom } from '../../services/nudge-state/index.tsx';
import { useRequestAccessErrorFlag } from '../request-access-error-flag/index.tsx';
import {
	getAnalyticsAttributes,
	getLoomCJNMessageStrings,
	useRequestAccessPromptFlag,
} from '../request-access-prompt-flag/index.tsx';
import { useRequestAccessSuccessFlag } from '../request-access-success-flag/index.tsx';
import { MAX_FEATURE_VISIT_COUNT } from './common.tsx';

const MAX_NUMBER_OF_LOOM_COLLABORATORS = 10;

const openLoomInANewTab = () => {
	if (typeof globalThis === 'undefined') return;
	const newTab = globalThis.window?.open('https://www.loom.com/spaces', '_blank');
	if (newTab) {
		newTab.focus();
	}
};

export const useLoomCrossJoinNudge = (): (() => Promise<void>) => {
	const cloudId = useCloudId();
	const userId = useAccountId() || '';
	const userFullname = useUserFullname();
	const [{ data: atlassianAccountData }] = useAtlassianAccount();
	const userEmail = atlassianAccountData?.email;
	const currentProduct = useCurrentProduct();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const getFilteredCollaborators = useFilteredUsersFromCollaborationGraph(
		cloudId,
		'loom',
		MAX_NUMBER_OF_LOOM_COLLABORATORS,
	);
	const showRequestAccessPromptFlag = useRequestAccessPromptFlag();
	const showSuccessFlag = useRequestAccessSuccessFlag();
	const showErrorFlag = useRequestAccessErrorFlag();

	const { snoozeFor7Days, snoozeFor30Days, getVisitedCount, increaseVisitedCount } = useSnooze(
		'crossJoinNudgesJiraToLoom',
	);
	const { getNudgeState, setNudgeDismissed } = useNudgeStateJiraToLoom();
	const requestAccessService = useRequestAccessService();

	return useCallback(async () => {
		try {
			const startTimestamp = Date.now();
			const onSubmit = async () => {
				try {
					const nonContextualNote = fg('twcg-1402-non-contextual-ra-note-gate')
						? buildNonContextualNote({
								fullName: userFullname,
								userEmail,
								targetProduct: 'loom',
								formatMessage,
							})
						: undefined;

					let accessStatus: AccessRequestType;

					if (requestAccessService) {
						const serviceResult = await requestAccessService({
							product: 'loom',
							integration: 'crossJoinNudgesJiraToLoom',
							touchpoint: 'Crossjoin Nudge',
							...(nonContextualNote && { note: nonContextualNote }),
						});

						if (serviceResult.error) {
							fireErrorAnalytics('crossJoinNudgesJiraToLoom', serviceResult.error, { cloudId });
							throw serviceResult.error;
						}

						accessStatus = serviceResult.result;
					} else {
						accessStatus = await requestAccess(
							'loom',
							currentProduct,
							cloudId,
							userId,
							'crossJoinNudgesJiraToLoom',
							createAnalyticsEvent,
							nonContextualNote,
						);
					}

					if (
						accessStatus === 'ACCESS_GRANTED' ||
						accessStatus === 'ACCESS_EXISTS' ||
						accessStatus === 'APPROVED_REQUEST_EXISTS'
					) {
						// This is an exception as the eligibilty check fails when user has access to loom
						openLoomInANewTab();
					} else if (
						accessStatus === 'PENDING_REQUEST_CREATED' ||
						accessStatus === 'PENDING_REQUEST_EXISTS'
					) {
						showSuccessFlag();
					} else {
						showErrorFlag('loom');
					}
				} catch {
					showErrorFlag('loom');
				}
			};

			const isUserOptOut = (await getNudgeState()) === 'DISMISSED';
			const featureVisitedCount = await getVisitedCount();
			if (isUserOptOut || featureVisitedCount >= MAX_FEATURE_VISIT_COUNT) return;

			// These snooze functions are for the case when user does not interact with the nudge
			if (featureVisitedCount === 0) {
				snoozeFor7Days();
			} else if (featureVisitedCount > 0) {
				snoozeFor30Days();
			}

			const collaboratorsOnLoom = await getFilteredCollaborators();

			const onDisplayed = () => {
				// oxlint-disable-next-line @typescript-eslint/no-explicit-any
				increaseVisitedCount(featureVisitedCount + 1).catch((error: any) => {
					if (!isErrorIgnoredForSLO(error)) {
						fireErrorAnalytics('crossJoinNudgesJiraToLoom', error, {
							cloudId,
							jiraToLoomCrossJoinNudgesErrorType: 'nudgeViewedFailed',
						});
					}
				});
			};

			const promptFlagArgs = {
				users: collaboratorsOnLoom,
				onSubmit,
				onOptout: setNudgeDismissed,
				snoozeFor7Days,
				snoozeFor30Days,
				integration: 'crossJoinNudgesJiraToLoom' as const,
				featureVisitedCount,
				messageParser: getLoomCJNMessageStrings({
					collaborators: collaboratorsOnLoom,
				}),
				eventAttributes: getAnalyticsAttributes({
					userId,
					users: collaboratorsOnLoom,
					featureVisitedCount,
					startTimestamp,
				}),
			};

			if (expVal('twcg-1920-fix-orcherstrator-block-handling', 'isEnabled', false)) {
				showRequestAccessPromptFlag({ ...promptFlagArgs, onDisplayed });
			} else {
				showRequestAccessPromptFlag(promptFlagArgs);
				increaseVisitedCount(featureVisitedCount + 1);
			}
			// oxlint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			if (!isErrorIgnoredForSLO(error)) {
				fireErrorAnalytics('crossJoinNudgesJiraToLoom', error, {
					cloudId,
					jiraToLoomCrossJoinNudgesErrorType: 'nudgeViewedFailed',
				});
			}
		}
	}, [
		cloudId,
		currentProduct,
		createAnalyticsEvent,
		formatMessage,
		getFilteredCollaborators,
		getNudgeState,
		getVisitedCount,
		increaseVisitedCount,
		requestAccessService,
		setNudgeDismissed,
		showErrorFlag,
		showRequestAccessPromptFlag,
		showSuccessFlag,
		snoozeFor7Days,
		snoozeFor30Days,
		userId,
		userFullname,
		userEmail,
	]);
};

const isErrorIgnoredForSLO = (error: unknown) => {
	const status = isFetchError(error) ? error.statusCode : null;
	return isClientFetchError(error) || status === 401 || status === 403;
};
